// apps/hr/src/App.js
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import HRLayout from './layouts/HRLayout';
import ProtectedRoute from './components/ProtectedRoute';

// ---- Login Page (root pages) ----
import HrLogin from './pages/HrLogin';

// ---- HR Pages (all from pages/hr/) ----
import Dashboard from './pages/hr/Dashboard';
import Employees from './pages/hr/Employees';
import EmployeeForm from './pages/hr/EmployeeForm';
import Attendance from './pages/hr/Attendance';
import Leave from './pages/hr/Leave';
import Salary from './pages/hr/Salary';
import Promotions from './pages/hr/Promotions';
import Expenses from './pages/hr/Expenses';
import Reports from './pages/hr/Reports';
import LeaveRequests from './pages/hr/LeaveRequests';
import MonthlyReport from './pages/hr/MonthlyReport';
import Recruitment from './pages/hr/Recruitment';
// ✅ Import the new FreelancerEarnings page
import FreelancerEarnings from './pages/hr/FreelancerEarnings';

// ---- (Optional) Job Applications & Openings – if you have them and want routes ----
// import JobApplications from './pages/hr/JobApplications';
// import JobOpenings from './pages/hr/JobOpenings';

const queryClient = new QueryClient();

const AppRoutes = () => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated && location.pathname !== '/login') {
    return <Navigate to="/login" replace />;
  }
  // Redirect to dashboard if already logged in and on login page
  if (isAuthenticated && location.pathname === '/login') {
    return <Navigate to="/" replace />;
  }

  return (
    <Routes>
      {/* Public Login */}
      <Route path="/login" element={<HrLogin />} />

      {/* Protected HR Routes (all under /) */}
      <Route path="/" element={<ProtectedRoute><HRLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="employees" element={<Employees />} />
        <Route path="employees/new" element={<EmployeeForm />} />
        <Route path="employees/edit/:id" element={<EmployeeForm />} />
        <Route path="attendance" element={<Attendance />} />
        <Route path="leave" element={<Leave />} />
        <Route path="salary" element={<Salary />} />
        <Route path="promotions" element={<Promotions />} />
        <Route path="expenses" element={<Expenses />} />
        <Route path="reports" element={<Reports />} />

        {/* New / Additional Pages */}
        <Route path="leave-requests" element={<LeaveRequests />} />
        <Route path="monthly-report" element={<MonthlyReport />} />
        <Route path="recruitment" element={<Recruitment />} />
        
        {/* ✅ New Route for Freelancer Earnings */}
        <Route path="freelancer-earnings" element={<FreelancerEarnings />} />

        {/* Optional: If you have JobApplications & JobOpenings pages, uncomment */}
        {/* <Route path="job-applications" element={<JobApplications />} /> */}
        {/* <Route path="job-openings" element={<JobOpenings />} /> */}
      </Route>

      {/* Catch-all redirect to dashboard */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter basename="/">
          <AppRoutes />
        </BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;